import { AlertTriangle, KeyRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { DashboardUserProfile } from '@services/firebase'
import {
  deriveLearnerAccessStatus,
  formatLearnerAccessDate,
} from '@features/premium/shared/learner-access-status'
import { LmsButton } from '@shared/ui'
import '../profile-layout.css'

export function ProfileSubscriptionSummary({
  onBuyKey,
  onEnterKey,
  profile,
}: {
  readonly onBuyKey: () => void
  readonly onEnterKey: () => void
  readonly profile: DashboardUserProfile
}) {
  const { i18n, t } = useTranslation('translation')
  const accessStatus = deriveLearnerAccessStatus({ locale: i18n.language, profile, t })
  const activationKey = accessStatus.activationKey
  const shouldShowNotice =
    accessStatus.state !== 'ACTIVE' && accessStatus.tone !== 'success'
  const unknownLabel = t('Unknown', { defaultValue: 'Unknown' })
  const planLabel =
    activationKey?.type === 'trial'
      ? t('Free trial (14 days)', { defaultValue: 'Free trial (14 days)' })
      : activationKey?.duration
        ? t('{{months}} months', {
            count: activationKey.duration,
            defaultValue: '{{months}} months',
            months: activationKey.duration,
          })
        : unknownLabel
  const formatDate = (value: number | null | undefined) =>
    formatLearnerAccessDate(value, i18n.language, t)

  return (
    <div className="profileCardStack">
      <div className="profileSectionStack">
        <h2 className="profileCardTitle">{t('Subscription')}</h2>
        <p className="profileMutedText">
          {t('Manage your activation keys or buy a premium plan.', {
            defaultValue: 'Manage your activation keys or buy a premium plan.',
          })}
        </p>
      </div>

      <div className="profileSectionStack">
        {shouldShowNotice ? (
          <div
            className={`profileSubscriptionNotice is-${
              accessStatus.tone === 'danger' ? 'urgent' : 'warning'
            }`}
            role="alert"
          >
            <AlertTriangle aria-hidden="true" className="profileSubscriptionNoticeIcon" />
            <div>
              <p className="profileSubscriptionNoticeTitle">
                {accessStatus.noticeTitle}
              </p>
              <p className="profileSubscriptionNoticeBody">
                {accessStatus.noticeBody}
              </p>
            </div>
          </div>
        ) : null}

        <div className="profileSubscriptionGrid">
          <div className="profileSubscriptionMetric">
            <span className="profileSubscriptionMetricLabel">
              {t('Registration date', { defaultValue: 'Registration date' })}
            </span>
            <p className="profileSubscriptionMetricValue">
              {formatDate(profile.createdAt)}
            </p>
          </div>
          <div className="profileSubscriptionMetric">
            <span className="profileSubscriptionMetricLabel">
              {t('Subscription plan', { defaultValue: 'Subscription plan' })}
            </span>
            <p className="profileSubscriptionMetricValue">
              {planLabel}
            </p>
          </div>
          <div className="profileSubscriptionMetric">
            <span className="profileSubscriptionMetricLabel">
              {t('Activation start', { defaultValue: 'Activation start' })}
            </span>
            <p className="profileSubscriptionMetricValue">
              {formatDate(activationKey?.startAt)}
            </p>
          </div>
          <div className="profileSubscriptionMetric">
            <span className="profileSubscriptionMetricLabel">
              {t('Course access expires', { defaultValue: 'Course access expires' })}
            </span>
            <p className="profileSubscriptionMetricValue">
              {formatDate(activationKey?.endAt)}
            </p>
          </div>
        </div>

        <div className="profileSubscriptionHistory">
          <h3 className="profileSubscriptionHistoryTitle">
            {t('Latest activation record', { defaultValue: 'Latest activation record' })}
          </h3>
          {accessStatus.hasActivationRecord ? (
            <dl className="profileSubscriptionHistoryList">
              <div>
                <dt>{t('Key number', { defaultValue: 'Key number' })}</dt>
                <dd>{activationKey?.keyNumber ?? unknownLabel}</dd>
              </div>
              <div>
                <dt>{t('Status', { defaultValue: 'Status' })}</dt>
                <dd>{accessStatus.label}</dd>
              </div>
              <div>
                <dt>{t('Activated on', { defaultValue: 'Activated on' })}</dt>
                <dd>{formatDate(activationKey?.startAt)}</dd>
              </div>
              <div>
                <dt>{t('Ends on', { defaultValue: 'Ends on' })}</dt>
                <dd>{formatDate(activationKey?.endAt)}</dd>
              </div>
            </dl>
          ) : (
            <p className="profileMutedText">
              {t('No activation history is available for this account yet.', {
                defaultValue: 'No activation history is available for this account yet.',
              })}
            </p>
          )}
        </div>
      </div>

      <div className="profileActionRow">
        <LmsButton
          leadingIcon={<KeyRound aria-hidden="true" className="size-4" />}
          mobileFullWidth
          onClick={onEnterKey}
          variant={accessStatus.state === 'ACTIVE' ? 'secondary' : 'primary'}
        >
          {t('Active account', { defaultValue: 'Active account' })}
        </LmsButton>
        <LmsButton
          mobileFullWidth
          onClick={onBuyKey}
          variant={accessStatus.state === 'ACTIVE' ? 'outline' : 'accent'}
        >
          {t('Buy a key', { defaultValue: 'Buy a key' })}
        </LmsButton>
      </div>
    </div>
  )
}
